import { useMap } from '@vis.gl/react-google-maps'
import { omit, uniqueId } from 'lodash'
import { useCallback, useState } from 'react'

import { CComMarker } from '#/components/Maps/Markers/CCom'
import { CEnvMarker } from '#/components/Maps/Markers/CEnv'
import { FarmMarker } from '#/components/Maps/Markers/Farm'
import FindMyCowMarker from '#/components/Maps/Markers/FindMyCow'
import { PinMarker } from '#/components/Maps/Markers/Pin'
import { MarkersProps } from '#/components/Maps/Markers/types'
import { useAutoFitBounds } from '#/components/Maps/Utils/AutoFitBounds'
import { useClusterer } from '#/components/Maps/Utils/useClusterer'

type ClusterMarkers = Record<string, google.maps.marker.AdvancedMarkerElement>

// Filtra os markers do cluster para manter apenas os que ainda existem nos dados atuais
function filterClusterMarkers(markers: MarkersProps['markers'], clusterMarkers: ClusterMarkers) {
  if (!markers) return {}

  const ids = Object.values(omit(markers, ['find_my_cow', 'pin']))
    .flat()
    .filter(Boolean)
    .map(item => String(item?.id))

  return Object.fromEntries(
    Object.entries(clusterMarkers).filter(([key]) => ids.includes(String(key))),
  )
}

export default function MapMarkers({ id, markers }: MarkersProps) {
  // Obtém a instância do mapa pelo id
  const map = useMap(id)

  // Estado para armazenar os markers que serão agrupados pelo cluster
  const [clusterMarkers, setClusterMarkers] = useState<ClusterMarkers>({})

  // Callback para registrar ou remover referências dos markers no estado
  const setMarkerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement, key: string) => {
      setClusterMarkers(prevState => {
        // Evita atualizações desnecessárias se o marker não mudou
        if ((marker && prevState[key]) || (!marker && !prevState[key])) return prevState

        if (marker) {
          // Adiciona ou atualiza marker
          return { ...prevState, [key]: marker }
        } else {
          // Remove marker do estado
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [key]: _, ...newMarkers } = prevState
          return newMarkers
        }
      })
    },
    [],
  )

  // Garante que só markers válidos e presentes nos dados atuais sejam enviados ao cluster
  const filteredClusterMarkers = filterClusterMarkers(markers, clusterMarkers)

  // Inicializa e sincroniza o clusterer com os markers filtrados
  useClusterer(map, filteredClusterMarkers)

  // Ajusta o mapa para mostrar todos os markers
  useAutoFitBounds({ map, markers })

  // Renderiza os markers individuais, passando o setMarkerRef para cada um
  return (
    <>
      {markers?.ccom?.map(marker => (
        <CComMarker key={marker.id} marker={marker} setMarkerRef={setMarkerRef} />
      ))}
      {markers?.farm?.map(marker => (
        <FarmMarker key={marker.id} marker={marker} setMarkerRef={setMarkerRef} />
      ))}
      {markers?.cenv?.map(marker => (
        <CEnvMarker key={marker.id} marker={marker} setMarkerRef={setMarkerRef} />
      ))}
      {markers?.pin?.map((marker, index) => (
        <PinMarker key={uniqueId(String(index))} marker={marker} map={map} />
      ))}
      {markers?.find_my_cow?.map((marker, index) => (
        <FindMyCowMarker key={uniqueId(String(index))} marker={marker} map={map} />
      ))}
    </>
  )
}
